/**
 * Toucan People — what the browser calls each page. The joining of the app's
 * name and the page's is the platform's; this file is only about what each
 * route's page is called.
 */

import { documentTitle, required } from "toucan";
import { tagById, tagLabel } from "./model.ts";
import {
  personById,
  reviewTagId,
  routePersonId,
  routeTagId,
} from "./routes.ts";
import { store } from "./state.ts";
import type { Route } from "./routes.ts";

/** What every page's title opens with. */
const APP = "Toucan People";

/** The page's own name, or "" where it has none to add — the list. */
function pageName(route: Route): string {
  const personId = routePersonId(route);
  if (personId !== undefined) {
    // Usually a page is only drawn for a record the store still has; one
    // that has just gone — here, or synced away on another device — falls
    // back to the app's own name until the redirect is done.
    return personById(personId)?.name ?? "";
  }
  const tagId = routeTagId(route);
  if (tagId !== undefined) {
    // The shell shows everybody for a tag that has gone, so the route only
    // ever names one the store holds.
    return tagLabel(required(tagById(store().tags, tagId), "tag"));
  }
  if (route.name === "add") {
    return "Add a person";
  }
  if (route.name === "review") {
    return reviewName(route);
  }
  if (route.name === "tags") {
    return "Manage tags";
  }
  if (route.name === "import") {
    return "Import people";
  }
  if (route.name === "settings") {
    return "Settings";
  }
  return "";
}

/**
 * A review session's name in the tab and at the top of the page, which is
 * Learn or Revise and the tag it is over, where it is over one.
 */
export function reviewName(route: Extract<Route, { name: "review" }>): string {
  const kind = route.kind === "learn" ? "Learn" : "Revise";
  const tagId = reviewTagId(route);
  if (tagId === undefined) {
    return kind;
  }
  return `${kind} · ${tagLabel(required(tagById(store().tags, tagId), "tag"))}`;
}

/** What the browser should be calling the page this route shows. */
export function pageTitle(route: Route): string {
  return documentTitle(APP, pageName(route));
}
